<script lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { HTMLAttributes } from 'vue';

import { cn } from '@/utils';
import * as typeform from '@typeform/embed';
import '@typeform/embed/build/css/popup.css';
import { MixpanelEvents, MixpanelProps } from '~/core/service/mixpanel';
import TextLink from '../ui/text-link/TextLink.vue';
const typeformPopup = ref<ReturnType<typeof typeform.createPopup>>();

const openTypeForm = () => {
	typeformPopup.value?.open();
};

export interface NavigationSectionProps {
	items?: NavigationSectionItem[];
	card?: {
		title: string;
		description: string;
		links: NavigationSectionLink[];
	} | null;
	color?: TitleVariants['color'];
	linksTitleClasses?: HTMLAttributes['class'];
	linksContainerVariant?: LinksContainerVariants['variant'];
}
</script>

<script setup lang="ts">
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import { GraphicsTriangle } from '~/components/Graphics/Triangle';
import type {
	LinksContainerVariants,
	NavigationSectionItem,
	NavigationSectionLink,
	TitleVariants
} from '.';

import {
	mailSouPilar,
	pilarFormattedPhone,
	pilarFormattedPhoneWhatsAppLink,
	urlSouPilar
} from '~/constants';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { labelVariants, linksContainerVariants, titleVariants } from '.';
const route = useRoute();
const { $mixpanel } = useNuxtApp();
const { partnerTypeformId } = useRuntimeConfig().public;

onMounted(() => {
	typeformPopup.value = typeform.createPopup(partnerTypeformId);
});

const addEvents = (
	link: NavigationSectionLink,
	item: NavigationSectionItem
) => {
	$mixpanel.track(MixpanelEvents.LINK_CLICKED, {
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: 'footer',
		[MixpanelProps.DESTINATION_URL]: link.route,
		[MixpanelProps.LINK_CATEGORY]: item.title,
		[MixpanelProps.LINK_TEXT]: link.label
	});
};

const getLinkAttributes = (
	link: NavigationSectionLink,
	item: NavigationSectionItem
) => {
	if (link.action) {
		return {
			onClick: () => {
				addEvents(link, item);
				if (link.action) {
					link.action();
				}
			}
		};
	}
	return {
		onClick: () => addEvents(link, item),
		href: link.route,
		target: '_blank',
		rel: 'noopener noreferrer'
	};
};

withDefaults(defineProps<NavigationSectionProps>(), {
	color: 'white',
	linksContainerVariant: 'default',
	items: () => [
		{
			title: 'Sobre nós',
			links: [
				{ label: 'Sobre a Pilar', route: '/sobre-a-pilar' },
				{
					label: 'Seja um Parceiro Pilar',
					action: openTypeForm
				},
				{ label: 'Conheça a Rede Pilar', route: urlSouPilar }
			]
		},
		{
			title: 'Comprar e Vender',
			links: [
				{
					label: 'Exclusivos PilarHomes',
					route: PropertyFiltersPresenter.getRouteObjectFromFilters({
						states: ['SP'],
						cities: ['São Paulo'],
						isExclusive: true
					})
				},
				{ label: 'Anunciar meu Imóvel', route: '/anunciar-imovel' },
				{ label: 'Off-Market', route: '/off-market' }
			]
		},
		{
			title: 'Fale conosco',
			links: [
				{
					label: mailSouPilar,
					route: `mailto:${mailSouPilar}`
				},
				{
					label: pilarFormattedPhone,
					route: pilarFormattedPhoneWhatsAppLink
				}
			],
			legend: 'Segunda-feira a sexta-feira das 9:00 às 18:00'
		}
	],
	card: () => ({
		title: 'Transformando o Mercado Imobiliário',
		description:
			'Conectamos compradores e vendedores a corretores especialistas com acesso ao maior portfólio de imóveis de alto padrão de São Paulo.',
		links: [{ label: 'Sobre a Pilar', route: '/sobre-a-pilar' }]
	})
});

function isInternalLink(link: RouteLocationRaw | undefined): boolean {
	return typeof link === 'string'
		? link.startsWith('/')
		: !!link?.path?.startsWith('/');
}
</script>

<template>
	<section
		class="flex flex-col gap-y-8 py-11 lg:flex-row lg:justify-between lg:gap-x-6"
	>
		<div
			v-for="item in items"
			:key="`navigation-section: ${item.title}`"
			:class="linksContainerVariants({ variant: linksContainerVariant })"
		>
			<p :class="[titleVariants({ color }), cn('', linksTitleClasses)]">
				{{ item.title }}
			</p>

			<div
				v-for="link in item.links"
				:key="`section:${item.title}_link:${link.label}`"
			>
				<NuxtLink
					v-if="isInternalLink(link.route)"
					:to="link.route"
					@click="addEvents(link, item)"
				>
					<TextLink as="p" variant="dark" :class="labelVariants({ color })">
						{{ link.label }}
					</TextLink>
				</NuxtLink>
				<a v-else v-bind="getLinkAttributes(link, item)">
					<TextLink as="p" variant="dark" :class="labelVariants({ color })">
						{{ link.label }}
					</TextLink>
				</a>
			</div>

			<p v-if="item.legend" class="text-xs text-white-dark-4">
				{{ item.legend }}
			</p>
		</div>

		<Card v-if="card" class="relative w-full bg-white lg:max-w-[302px]">
			<CardHeader class="px-6 pb-3 pt-6">
				<CardTitle class="text-start text-2xl leading-[150%] text-primary">
					{{ card.title }}
				</CardTitle>
				<CardDescription
					class="text-start font-normal leading-[150%] text-primary"
				>
					{{ card.description }}
				</CardDescription>
				<div class="absolute right-4 top-4 size-5">
					<GraphicsTriangle />
				</div>
			</CardHeader>

			<CardContent class="flex flex-col items-start gap-y-3 px-6 pb-6">
				<NuxtLink
					v-for="link in card.links"
					:key="`footer-card-link:${link.route}`"
					:to="link.route"
				>
					<TextLink
						as="p"
						class="text-base font-normal leading-[150%] text-primary underline decoration-black/20 underline-offset-8"
					>
						{{ link.label }}
					</TextLink>
				</NuxtLink>
			</CardContent>
		</Card>
	</section>
</template>
